import { useEffect, useState } from "react";
import { Grid, Typography } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Pane } from "src/components/Pane";
import {
  PointSourceEmissionImportForm,
  PointSourceEmissionImportFormData,
} from "src/forms/PointSourceEmissionImportForm";
import {
  PointSourceEmissionImportMutationData,
  usePointSourceEmissionImportMutation,
} from "src/hooks/usePointSourceEmissionImport";
import { usePointSourceEmision } from "src/hooks/usePointSourceEmission";
import { ImportStatus, PointSourceEmission } from "src/types";
import { ImportFailed } from "src/components/ImportFailed";
import { ImportFinished } from "src/components/ImportFinished";
import { usePrevious } from "src/hooks";

interface Props {}

export function PointSourceEmissionImportPage(props: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    caseId = "",
    scenarioId = "",
    emissionId = "",
  } = useParams<{
    caseId: string;
    scenarioId: string;
    emissionId: string;
  }>();

  const [refetchInterval, setRefetchInterval] = useState<false | number>(false);

  const { data: emission } = usePointSourceEmision(scenarioId, emissionId, { refetchInterval });
  const previousImportStatus = usePrevious(emission?.importStatus);

  const importMutation = usePointSourceEmissionImportMutation(scenarioId, emissionId);

  /**
   * Effects
   */
  useEffect(updateRefreshInterval, [emission]);
  useEffect(actUponFinishedImportStatus, [navigate, location, previousImportStatus, emission]);

  /**
   * Functions
   */
  function updateRefreshInterval() {
    if (emission?.importStatus === ImportStatus.IMPORTING) {
      setRefetchInterval(1000);
    } else setRefetchInterval(false);
  }

  function actUponFinishedImportStatus() {
    // first check the previous import status
    if (previousImportStatus === undefined) return;
    if (previousImportStatus === ImportStatus.FINISHED) return;

    // if the new status is finished, act upon it
    if (emission?.importStatus === ImportStatus.FINISHED) {
      navigate({ ...location }, { state: { action: "rerenderWMS" } });
    }
  }

  async function handleSave(data: PointSourceEmissionImportFormData) {
    if (!data.pointSourceFile) {
      console.warn("No 'pointSourceFile' field submitted, this should be fixed.");
      return;
    }
    await importMutation.mutateAsync(data as PointSourceEmissionImportMutationData);
  }

  function handleClose() {
    navigate(`/cases/${caseId}/scenarios/${scenarioId}/emissions/point-source/${emissionId}`);
  }

  // render the component
  return render();

  function render() {
    if (!emission) return null;

    return (
      <Pane width={400}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h2">{t("label.bulkUploadPointSourceEmissionData")}</Typography>
          </Grid>

          {emission.importStatus !== ImportStatus.IMPORTING && renderForm(emission)}

          {emission.importStatus === ImportStatus.IMPORTING && (
            <Grid item xs={12}>
              <Typography variant="h2">{t(`label.importStatus.${ImportStatus.IMPORTING}`)}</Typography>
            </Grid>
          )}
        </Grid>
      </Pane>
    );
  }

  function renderForm(emission: PointSourceEmission) {
    return (
      <Grid item xs={12}>
        <PointSourceEmissionImportForm
          key={emission.importStatus}
          data={{ pointSourceFile: null }}
          onClose={handleClose}
          onSubmit={handleSave}
          isLoading={importMutation.isLoading}
          errors={importMutation.error?.response?.data}
          statusBody={
            <Grid item xs={12}>
              {renderStatusBody(emission)}
            </Grid>
          }
        />
      </Grid>
    );
  }

  function renderStatusBody(emission: PointSourceEmission) {
    if (emission.importStatus === ImportStatus.FINISHED)
      return <ImportFinished importWarnings={emission.importWarnings} />;
    if (emission.importStatus === ImportStatus.FAILED) return <ImportFailed importErrors={emission.importErrors} />;
    else return null;
  }
}
